import { useEffect, useState } from 'react';
import { Save } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useTaskStore } from '../../stores/taskStore';

export default function PreInstructionsTab() {
  const preInstructions = useTaskStore((s) => s.preInstructions);
  const preInstructionsLoading = useTaskStore((s) => s.preInstructionsLoading);
  const fetchPreInstructions = useTaskStore((s) => s.fetchPreInstructions);
  const updatePreInstructions = useTaskStore((s) => s.updatePreInstructions);

  const [content, setContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    fetchPreInstructions();
  }, [fetchPreInstructions]);

  useEffect(() => {
    if (preInstructions) {
      setContent(preInstructions.content);
      setDirty(false);
    }
  }, [preInstructions]);

  function handleChange(value: string) {
    setContent(value);
    setDirty(value !== (preInstructions?.content || ''));
  }

  async function handleSave() {
    setSaving(true);
    try {
      await updatePreInstructions(content);
      setDirty(false);
    } catch (err) {
      console.error('Failed to save pre-instructions:', err);
    } finally {
      setSaving(false);
    }
  }

  if (preInstructionsLoading) {
    return (
      <div className="text-center py-12 text-text-muted text-sm">Loading pre-instructions...</div>
    );
  }

  return (
    <div className="flex flex-col gap-4 max-w-3xl">
      {/* Header */}
      <div>
        <h2 className="text-sm font-medium text-text-primary mb-1">Pre-instructions</h2>
        <p className="text-xs text-text-muted">
          These instructions are prepended to every task before it is dispatched to an agent.
          Edit them below and save when ready.
        </p>
      </div>

      {/* Editor */}
      <textarea
        value={content}
        onChange={(e) => handleChange(e.target.value)}
        className="bg-bg-tertiary border border-border-subtle text-text-primary rounded-lg px-4 py-3 w-full text-sm focus:outline-none focus:border-accent-purple resize-y font-mono leading-relaxed"
        rows={16}
        placeholder="Enter pre-instructions..."
      />

      {/* Footer */}
      <div className="flex items-center justify-between">
        <div className="text-xs text-text-muted">
          {preInstructions?.updated_at && (
            <span>
              Last updated {formatDistanceToNow(new Date(preInstructions.updated_at), { addSuffix: true })}
            </span>
          )}
        </div>

        <button
          onClick={handleSave}
          disabled={saving || !dirty}
          className="bg-accent-purple text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-accent-purple/80 transition-colors disabled:opacity-50 flex items-center gap-2"
        >
          <Save className="h-4 w-4" />
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  );
}
